import React from 'react';
import type { CartLine } from '../../useSeller';
import { isPrintableKey } from '../../printableKey';

// Unlike `useSeller`'s own `clampQnt` (which allows 0, meaning "remove the
// line"), `+`/`-` must never reach 0 — a floor of 1 keeps a `-` at the last
// unit a no-op instead of deleting the line out from under the seller.
const clampStepQnt = (qnt: number, stock: number): number =>
    Math.max(1, Math.min(qnt, stock));

type RowKey = '+' | '-' | 'Delete';

/**
 * `+`, `-` or `Delete` pressed inside a line. Shift is allowed (`+` needs it
 * on most layouts); Ctrl, Alt and Meta are not — `Ctrl+Delete` is the void
 * shortcut, `Ctrl+-`/`Ctrl++` the browser's zoom.
 */
const rowKeyOf = (event: React.KeyboardEvent): RowKey | null => {
    if (event.ctrlKey || event.altKey || event.metaKey) {
        return null;
    }
    return event.key === '+' || event.key === '-' || event.key === 'Delete'
        ? event.key
        : null;
};

/** Keys the stepper's hidden field spins its value with, without editing its text: `Home`/`End` jump to the stock bounds, `PageUp`/`PageDown` step it. */
const FIELD_SPIN_KEYS = new Set(['Home', 'End', 'PageUp', 'PageDown']);

/** The variant id of the line `target` sits in — the row itself or anything inside it. */
const lineIdOf = (target: EventTarget | null): string | null =>
    target instanceof HTMLElement
        ? (target.closest<HTMLElement>('[data-cart-line]')?.dataset.cartLine ??
          null)
        : null;

/** The stepper's visually hidden text field: the only input a line has. */
const isStepperField = (target: EventTarget | null): boolean =>
    target instanceof HTMLInputElement && lineIdOf(target) !== null;

/** Refuses an edit (a native `beforeinput`, a cut, a drop) aimed at the stepper's field. */
const refuseFieldEdit = (event: Pick<Event, 'target' | 'preventDefault'>) => {
    if (isStepperField(event.target)) {
        event.preventDefault();
    }
};

/**
 * Listens for native `beforeinput` on the list's wrapper, in the capture
 * phase: React's own `onBeforeInput` is a different, older event that
 * misses deletions such as `Ctrl+Backspace`. A ref callback with a cleanup,
 * so the listener lives exactly as long as the wrapper.
 */
const listenForFieldEdits = (node: HTMLElement | null) => {
    node?.addEventListener('beforeinput', refuseFieldEdit, true);
    return () =>
        node?.removeEventListener('beforeinput', refuseFieldEdit, true);
};

/**
 * The line to focus once `removedId` has left the cart: the nearest line
 * after it that is still there, else the nearest one before it — or `null`
 * when none is left. Several lines can go at once (a refresh), hence the
 * search rather than a plain index step.
 */
export const lineAfterRemoval = (
    previousCart: CartLine[],
    cart: CartLine[],
    removedId: string,
): string | null => {
    const remaining = new Set(cart.map((line) => line.variant.variantId));
    const index = previousCart.findIndex(
        (line) => line.variant.variantId === removedId,
    );
    const isRemaining = (line: CartLine) =>
        remaining.has(line.variant.variantId);
    const after = previousCart.slice(index + 1).find(isRemaining);
    const before = previousCart
        .slice(0, Math.max(index, 0))
        .reverse()
        .find(isRemaining);
    return (after ?? before)?.variant.variantId ?? null;
};

/** Focus was dropped: the focused element left the DOM and the browser fell back to `<body>`. */
const isFocusDropped = (active: Element | null): boolean =>
    active === null || active === document.body || !active.isConnected;

type UseCartKeyboardNavParams = {
    cart: CartLine[];
    setLineQnt: (variantId: string, qnt: number) => void;
    removeLine: (variantId: string) => void;
    search: string;
    setSearch: (value: string) => void;
    focusHome: () => void;
    /** Search itself, even while another control holds home (SEL-49). */
    focusSearch: () => void;
};

/**
 * Row-level `+`/`-`/`Delete`, the printable-key-to-search redirect, and
 * focus after a line leaves, for the cart's `ListView` grid list
 * (bos-seller#56 — the spike result on bos-seller#47 found the grid list
 * cooperates with the Pro number stepper and remove button, no fallback
 * needed). `listProps` goes on the list's wrapper: a capture-phase
 * `keydown` handler reads the line from the `data-cart-line` attribute each
 * `ListView.Item` carries — ahead of the design system's own row navigation
 * and typeahead, the same shape the product grid uses.
 *
 * Keys follow the line, not the element: on the row, on its stepper or
 * remove button, and on the stepper's visually hidden text field alike.
 * That field is a real focus stop — the row's `→` walk lands on it, and
 * react-aria's number field focuses it on a mouse press of `+`/`−` — but it
 * is never edited: a letter or digit (`isPrintableKey`, the rule the product
 * grid shares) goes to search, `+`/`-`/`Delete` act on the line, the keys
 * that would spin it (`Home`/`End`, `PageUp`/`PageDown`) are swallowed,
 * every edit is refused by input type (`beforeinput`, cut, drop — typed
 * text and `Backspace` with any modifier included), and pasted text goes to
 * search. Neither a scan, a paste nor `End` can set the quantity out of
 * sight. Row keys act once per press:
 * a held key's repeats are swallowed, so holding `Delete` removes one line.
 * Space and Enter are not search keys, so a focused remove button still
 * activates.
 *
 * Focus after removal is route-agnostic. Focus events on the wrapper note
 * which line holds focus; when that line leaves the cart by any route —
 * `Delete`, its remove button, a stock refresh — focus moves to the
 * nearest remaining line, else home.
 */
export const useCartKeyboardNav = ({
    cart,
    setLineQnt,
    removeLine,
    search,
    setSearch,
    focusHome,
    focusSearch,
}: UseCartKeyboardNavParams) => {
    const itemRefs = React.useRef(new Map<string, HTMLElement>());
    // The line holding focus, or `null` once focus has moved outside the
    // list. Read by the effect below after a line leaves the cart.
    const focusedLineRef = React.useRef<string | null>(null);
    // The cart as last committed, to find a removed line's neighbours.
    const previousCartRef = React.useRef(cart);

    const registerItemRef = React.useCallback(
        (variantId: string) => (node: HTMLElement | null) => {
            if (node) {
                itemRefs.current.set(variantId, node);
            } else {
                itemRefs.current.delete(variantId);
            }
        },
        [],
    );

    React.useEffect(() => {
        const previousCart = previousCartRef.current;
        previousCartRef.current = cart;
        const focusedLine = focusedLineRef.current;
        if (
            focusedLine === null ||
            cart.some((line) => line.variant.variantId === focusedLine)
        ) {
            return;
        }
        focusedLineRef.current = null;
        // The list re-renders its rows after the cart does, so the line's row
        // can still be on screen here with focus in it; or the row is gone
        // (the whole list, when the cart empties) and focus dropped with it.
        // Focus anywhere else has moved on, and stays where it is.
        const active = document.activeElement;
        const leavingRow = itemRefs.current.get(focusedLine);
        if (
            !(leavingRow?.contains(active) ?? false) &&
            !isFocusDropped(active)
        ) {
            return;
        }
        const next = lineAfterRemoval(previousCart, cart, focusedLine);
        if (next === null) {
            focusHome();
            return;
        }
        itemRefs.current.get(next)?.focus();
    }, [cart, focusHome]);

    /**
     * The variant id of the line holding focus (the row or anything inside
     * it), or `null` when focus is outside the list. Read at the moment a
     * key needs it (`F6`), so it is a getter over the same note the
     * focus-after-removal effect keeps, not state.
     */
    const getFocusedLine = React.useCallback(
        (): string | null => focusedLineRef.current,
        [],
    );

    /** `F4`: focus the last line. The binding is disabled while the cart is empty, so there is always one. */
    const focusLastLine = React.useCallback(() => {
        const lastLine = cart[cart.length - 1];
        if (lastLine) {
            itemRefs.current.get(lastLine.variant.variantId)?.focus();
        }
    }, [cart]);

    const onFocus = (event: React.FocusEvent<HTMLElement>) => {
        focusedLineRef.current = lineIdOf(event.target);
    };

    // Focus moving to another control outside the list forgets the line.
    // A blur with no destination is either the focused line being removed —
    // the effect above places focus then — or focus going nowhere (a click
    // on a blank area, another window): once the current task is done, a
    // removed line has left the document and a blank-area click has not.
    const onBlur = (event: React.FocusEvent<HTMLElement>) => {
        const destination = event.relatedTarget;
        if (destination instanceof Node) {
            if (!event.currentTarget.contains(destination)) {
                focusedLineRef.current = null;
            }
            return;
        }
        const blurred = event.target;
        queueMicrotask(() => {
            if (
                blurred.isConnected &&
                focusedLineRef.current === lineIdOf(blurred)
            ) {
                focusedLineRef.current = null;
            }
        });
    };

    const onPasteCapture = (event: React.ClipboardEvent<HTMLElement>) => {
        if (!isStepperField(event.target)) {
            return;
        }
        event.preventDefault();
        const text = event.clipboardData.getData('text').trim();
        if (text !== '') {
            setSearch(search + text);
            focusSearch();
        }
    };

    const onKeyDownCapture = (event: React.KeyboardEvent<HTMLElement>) => {
        const variantId = lineIdOf(event.target);
        const rowKey = variantId === null ? null : rowKeyOf(event);

        if (variantId !== null && rowKey !== null) {
            event.preventDefault();
            event.stopPropagation();
            const line = cart.find(
                (candidate) => candidate.variant.variantId === variantId,
            );
            if (event.repeat || !line) {
                return;
            }
            if (rowKey === 'Delete') {
                removeLine(variantId);
                return;
            }
            setLineQnt(
                variantId,
                clampStepQnt(
                    line.qnt + (rowKey === '+' ? 1 : -1),
                    line.variant.sellableQuantity,
                ),
            );
            return;
        }

        if (isPrintableKey(event.nativeEvent)) {
            event.preventDefault();
            event.stopPropagation();
            setSearch(search + event.key);
            focusSearch();
            return;
        }

        if (isStepperField(event.target) && FIELD_SPIN_KEYS.has(event.key)) {
            event.preventDefault();
            event.stopPropagation();
        }
    };

    return {
        registerItemRef,
        focusLastLine,
        getFocusedLine,
        listProps: {
            ref: listenForFieldEdits,
            onKeyDownCapture,
            onFocus,
            onBlur,
            onPasteCapture,
            onCutCapture: refuseFieldEdit,
            onDropCapture: refuseFieldEdit,
        },
    };
};
